import React, { useRef } from "react";
import { Button } from "@global";
import styled from "styled-components";
import { colors } from "@util/constants";
import type { UploadedFileRecord } from "@util/fileUploads";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function getRecordTypeDisplay(record: UploadedFileRecord): { type: string; icon: string } {
  if (record.mimeType.startsWith("image/")) return { type: "Image", icon: "🖼️" };
  if (record.mimeType === "application/pdf") return { type: "PDF", icon: "📄" };
  if (record.mimeType.includes("spreadsheet") || record.fileName.endsWith(".xls") || record.fileName.endsWith(".xlsx")) {
    return { type: "Spreadsheet", icon: "📊" };
  }
  if (record.mimeType.includes("word") || record.fileName.endsWith(".doc") || record.fileName.endsWith(".docx")) {
    return { type: "Word", icon: "📝" };
  }
  return { type: "Document", icon: "📁" };
}

const DropZone = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1.25rem;
  border: 1px dashed ${props => (props.$hasError ? "#e74c3c" : "#ccc")};
  border-radius: 8px;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: ${colors.flightBlue};
    background-color: rgba(36, 70, 142, 0.02);
  }

  input[type="file"] {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .button-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .dragndrop {
    font-size: 0.875rem;
    color: ${colors.textBody};
  }
`;

const FilePreview = styled.div`
  width: 240px;
  padding: 0.75rem;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: #fff;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .file-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  .file-img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .file-info {
    min-width: 0;
    flex: 1;
  }

  .file-name {
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0 0.25rem 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-meta {
    font-size: 0.75rem;
    color: ${colors.headingB};
    margin: 0;
  }

  .file-remove {
    flex-shrink: 0;
    background: none;
    border: none;
    color: #c0392b;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 1.25rem;
    line-height: 1;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  .file-remove:hover {
    background-color: rgba(192, 57, 43, 0.1);
  }

  .file-status {
    font-size: 0.75rem;
    margin-top: 0.3rem;
  }

  .file-status.uploading {
    color: ${colors.flightBlue};
  }

  .file-status.uploaded {
    color: #2e7d32;
  }

  .file-status.failed {
    color: #c0392b;
  }

  .file-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.35rem;
  }

  .file-action {
    border: none;
    background: none;
    color: ${colors.flightBlue};
    cursor: pointer;
    font-size: 0.75rem;
    padding: 0;
  }

  .file-action:hover {
    text-decoration: underline;
  }
`;

interface DragAndDropFileInputProps {
  id: string;
  name: string;
  accept?: string;
  value: UploadedFileRecord[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onRetryFile: (fileId: string) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  multiple?: boolean;
}

export function DragAndDropFileInput({
  id,
  name,
  accept,
  value,
  onAddFiles,
  onRemoveFile,
  onRetryFile,
  disabled = false,
  error = false,
  className,
  multiple = false,
}: DragAndDropFileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !e.dataTransfer.files.length) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    onAddFiles(multiple ? droppedFiles : droppedFiles.slice(0, 1));
    e.dataTransfer.clearData();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) onAddFiles(multiple ? selectedFiles : selectedFiles.slice(0, 1));

    // Clear input to allow selecting the same file again
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className={className}>
      <DropZone $hasError={error} onDragOver={handleDragOver} onDrop={handleDrop}>
        <input
          ref={inputRef}
          type="file"
          id={id}
          name={name}
          accept={accept}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={error}
          multiple={multiple}
        />
        <div className="button-area">
          <Button
            type="button"
            text={multiple ? "Choose files" : "Choose a file"}
            theme="base"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          />
          <span className="dragndrop">or drag {multiple ? "them" : "it"} here</span>
        </div>
      </DropZone>

      {value.length > 0 && (
        <div
          style={{
            marginTop: "0.75rem",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "stretch",
          }}
        >
          {value.map(record => {
            const statusText =
              record.status === "uploading"
                ? `Uploading (${Math.round(record.progress)}%)`
                : record.status === "uploaded"
                  ? "Uploaded"
                  : record.status === "failed"
                    ? record.error || "Upload failed"
                    : "Queued";

            return (
              <FilePreview key={record.id}>
                <span className="file-icon">{getRecordTypeDisplay(record).icon}</span>
                <div className="file-info">
                  <p className="file-name">{record.fileName}</p>
                  <p className="file-meta">
                    {getRecordTypeDisplay(record).type} · {formatFileSize(record.size)}
                  </p>
                  <p className={`file-status ${record.status}`}>{statusText}</p>
                  <div className="file-actions">
                    {record.status === "failed" && (
                      <button type="button" className="file-action" onClick={() => onRetryFile(record.id)}>
                        Retry
                      </button>
                    )}
                    {record.downloadUrl && (
                      <a href={record.downloadUrl} target="_blank" rel="noreferrer" className="file-action">
                        View
                      </a>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="file-remove"
                  onClick={() => onRemoveFile(record.id)}
                  title="Remove file"
                  aria-label="Remove file"
                >
                  ×
                </button>
              </FilePreview>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DragAndDropFileInput;
